import { useLocalSearchParams } from 'expo-router';

import { client, useQuery } from '@fhs/client';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { ICartEntry } from '@rbi-ctg/menu';

import type {
  ConvertedCartMenuItemComboItem,
  ConvertedCartMenuItemModifiersJson,
  CustomizationSelections,
} from '../types';

export type EditCartEntryResult = ReturnType<typeof useEditCartEntry>;

export function useEditCartEntry(editingCartEntry?: ICartEntry, isEnabled = true) {
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();
  const storeId = store?.number ?? '';

  const query = useQuery({
    enabled: Boolean(serviceMode && storeId && editingCartEntry && isEnabled),
    queryKey: [storeId, serviceMode, 'cartEntryToMenuItemConversion', editingCartEntry] as const,
    queryFn: async ({ queryKey }) => {
      const cartEntry = queryKey.at(-1);

      const response = await client.queries.convertToMenuItem({
        serviceMode,
        storeId,
        cartEntry: JSON.stringify(cartEntry),
      });

      // Parse unparsed json
      let modifiersJson = response.data?.modifiersJson;

      if (typeof modifiersJson === 'string') {
        modifiersJson = JSON.parse(modifiersJson);
      }

      let pickerSelections = response.data?.pickerSelections;

      if (typeof pickerSelections === 'string') {
        pickerSelections = JSON.parse(pickerSelections);
      }

      let comboItemsJson = response.data?.comboItemsJson;

      if (typeof comboItemsJson === 'string') {
        comboItemsJson = JSON.parse(comboItemsJson);
      }

      // Modifiers -> customization selections
      if (
        response.errors?.length ||
        !response.data ||
        // Only here to narrow the type... this just always be an array even if it's an empty one
        !Array.isArray(modifiersJson)
      ) {
        //@TODO: Handle these errors...
        return null;
      }

      const userCustomizationSelections =
        transformModifiersJsonToCustomizationSelections(modifiersJson);

      const comboItems = comboItemsJson ?? [];

      // Type narrowing
      if (!Array.isArray(comboItems)) {
        // Shouldn't happen?
        return null;
      }

      const transformedComboItems = (comboItems as Array<ConvertedCartMenuItemComboItem>).map(
        comboItem => {
          return {
            ...comboItem,
            customizationSelections: transformModifiersJsonToCustomizationSelections(
              comboItem.modifiersJson
            ),
            selectedCustomizationOptions: comboItem.modifiersJson.map(modifier => ({
              displayName: modifier.name,
              upChargeCents: modifier.price ?? 0,
            })),
          };
        }
      );

      const selectedPickerAspectOptionId: string | null =
        Object.values(pickerSelections ?? {})?.at(0) ?? null;

      return {
        instructions: response.data.instructions,
        selectedPickerAspectOptionId,
        userCustomizationSelections,
        quantity: response.data.quantity,
        isComboEnabled: response.data.isCombo,
        comboItems: transformedComboItems,
      };
    },
  });

  return {
    query,
    cartEntry: editingCartEntry,
  };
}

export function useEditFromCartState() {
  const { cartEntries, cartIdEditing } = useOrderContext();
  const localSearchParams = useLocalSearchParams<{ slug?: string }>();

  const foundCartEntry = cartEntries?.find(entry => entry.cartId === cartIdEditing);

  const isEditingThisItem =
    foundCartEntry && localSearchParams.slug && foundCartEntry.url.endsWith(localSearchParams.slug);
  const { query } = useEditCartEntry(foundCartEntry, isEditingThisItem);

  return {
    query,
    cartEntry: foundCartEntry,
    isEditingThisItem,
  };
}

function transformModifiersJsonToCustomizationSelections(
  modifiersJson: ConvertedCartMenuItemModifiersJson
): CustomizationSelections {
  const customizationSelections: CustomizationSelections = {};

  for (const customizationSelection of modifiersJson) {
    const [optionKey, selectionKey] = customizationSelection.id.split(':');

    if (
      optionKey in customizationSelections &&
      typeof customizationSelections[optionKey] === 'string'
    ) {
      const existingStringValue = customizationSelections[optionKey] as string;
      customizationSelections[optionKey] = {
        [existingStringValue]: 1,
        [selectionKey]: customizationSelection.quantity,
      };
      continue;
    }

    if (!customizationSelections[optionKey] && customizationSelection.quantity === 1) {
      customizationSelections[optionKey] = selectionKey;
      continue;
    }

    if (!customizationSelections[optionKey]) {
      customizationSelections[optionKey] = {};
    }

    customizationSelections[optionKey][selectionKey] = customizationSelection.quantity;
  }

  return customizationSelections;
}
